import { useEffect, useRef } from 'react'
import dynamic from 'next/dynamic'
import Image from 'next/legacy/image'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import styled from 'styled-components'
import { omit, pick } from 'ramda'
import { useDispatch } from 'react-redux'

import { breakpoints } from '@/styles'
import { initializeApollo } from '@/lib/apollo'
import {
  getCompanyData,
  getCompanyProfileById,
  useCompanyData,
  useCompanyProfile
} from '@/lib/company'
import { initializeStore } from '@/lib/redux'
import { getSession } from '@/lib/session'
import { stringify } from '@/utils/qs'
import useCurrentSession from '@/hooks/use-current-session'
import { useCompanyFiltersListener } from '@/hooks/use-filters'
import useMediaQuery from '@/hooks/use-media-query'
import useOnScreen from '@/hooks/use-onscreen'
import { makeMainLayout } from '@/layouts/main'
import { show as showLoginModal } from '@/features/Auth/login-modal'
import ListingList from '@/features/Company/list'
import Footer from '@/features/Footer'
import Header from '@/features/Header'
import { FiltersSettingsProvider } from '@/features/Listings/hooks/use-filters-settings'

const CitySelector = dynamic(() => import('@/features/CitySelector'))

const Wrapper = styled.div`
  position: relative;
`

const MobileSpacer = styled.div`
  height: 50px;

  @media screen and (max-width: 890px) {
    height: 200px;
  }
`

const SpacerWrapper = styled.div`
  display: none;

  @media screen and (max-width: 890px) {
    display: flex;
    justify-content: center;
    align-items: center;
  }
`

const SearchWrapper = styled.div`
  background-color: white;
  padding: 15px 25px 25px;
  z-index: 402;
`

const QrCode = styled.div`
  position: relative;
  width: 140px;
  height: 185px;
  margin-right: 30px;

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    margin-right: 15px;
  }
`

const Logo = styled.div`
  position: relative;
  width: 200px;
  height: 160px;

  @media only screen and (max-width: 400px) {
    width: 100px;
  }
`

const CompanyRoot = () => {
  const dispatch = useDispatch()
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  const session = useCurrentSession()
  const router = useRouter()
  const { t } = useTranslation()
  const companyId = router.query.id
  const companyProfile = useCompanyProfile(companyId)
  const companyData = useCompanyData(companyProfile?.id || companyId)
  useCompanyFiltersListener()

  // There are 3 types of company pages: VIP, Custom, Generic
  const isGenericPage = !companyProfile
  const isCustomPage = companyProfile?.pageType === 'custom'
  const isVipPage = companyProfile?.pageType === 'vip'

  useEffect(() => {
    if (session && session.uuid) {
      dispatch.sse.subscribe(session.uuid)
    }
    if (session?.registration_complete === '0') {
      showLoginModal({
        mode: 'register',
        t
      })
    }
    return () => dispatch.sse.unsubscribe()
  }, [dispatch.sse, session, t])

  useEffect(() => {
    dispatch.ga.setSource('Company Page')
  }, [dispatch.ga])

  // Hooks for QR Code positioning
  const footerRef = useRef()
  const listingsRef = useRef()
  const footerOnScreen = useOnScreen(footerRef, '0px')
  const listingsOnScreen = useOnScreen(listingsRef, '-290px')

  return (
    <>
      <Wrapper>
        {!isVipPage && (
          <>
            <Header showSearch />
            {displayMobile && (
              <SearchWrapper>
                <CitySelector instanceId="listings-select-city" />
              </SearchWrapper>
            )}
          </>
        )}

        <FiltersSettingsProvider
          initialFilters={{
            sort: { sort_by: 'CREATED_AT', sort_type: 'DESC' }
          }}
        >
          <ListingList
            companyData={companyData}
            companyId={companyProfile?.id || companyId}
            companyProfile={companyProfile}
            isGenericPage={isGenericPage}
            isCustomPage={isCustomPage}
            isVipPage={isVipPage}
            listingsRef={listingsRef}
            footerOnScreen={footerOnScreen}
            listingsOnScreen={listingsOnScreen}
          />
        </FiltersSettingsProvider>
        {!!(companyProfile?.qrAlt && companyProfile?.logoAlt) && (
          <MobileSpacer>
            <SpacerWrapper>
              <QrCode>
                <Image
                  src={companyProfile.qr}
                  alt={companyProfile.qrAlt}
                  placeholder="blur"
                  objectFit="contain"
                  layout="fill"
                />
              </QrCode>
              <Logo>
                <Image
                  src={companyProfile.logo}
                  alt={companyProfile.logoAlt}
                  placeholder="blur"
                  objectFit="contain"
                  layout="fill"
                />
              </Logo>
            </SpacerWrapper>
          </MobileSpacer>
        )}
        <div ref={footerRef} />
      </Wrapper>
      <Footer />
    </>
  )
}

const selectFromState = pick(['company', 'listings', 'session'])

export const getServerSideProps = async ({ req, query, locale, params }) => {
  const apolloClient = initializeApollo()
  const reduxStore = initializeStore()

  const companyId = query?.id
  const companyProfile = getCompanyProfileById(companyId)

  await Promise.all([
    getCompanyData({
      params,
      query,
      reduxStore,
      apolloClient
    }),
    getSession({ req, reduxStore })
  ])

  const url = companyProfile?.url

  const localeWithDefault = locale ?? 'en'
  const companyTranslationFiles = url
    ? ['translation', 'filters', url]
    : ['translation', 'filters']
  if (companyProfile && url !== companyId) {
    const params = stringify(omit(['id'], query))
    const end = params ? `?${params}` : ''
    return {
      redirect: {
        permanent: true,
        destination: `/company/${url}` + end
      }
    }
  }

  return {
    props: {
      initialApolloState: apolloClient.cache.extract(),
      initialReduxState: selectFromState(reduxStore.getState()),
      ...(await serverSideTranslations(
        localeWithDefault,
        companyTranslationFiles
      ))
    }
  }
}

const headerConfig = { visible: false }
const footerConfig = {
  visible: false
}
const supportConfig = {
  enabled: false
}

CompanyRoot.Layout = makeMainLayout({
  headerConfig,
  footerConfig,
  supportConfig
})

export default CompanyRoot
